import { FocusMonitor } from '@angular/cdk/a11y';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    ViewEncapsulation,
    computed,
    contentChildren,
    effect,
    inject,
    input,
    model,
    signal,
} from '@angular/core';

import { DS_ATOMS } from '@frontend/ui/shared';

export const DS_PILL_VARIANTS_ARRAY = ['current', 'subtle', 'strong'] as const;
export type DsPillVariant = (typeof DS_PILL_VARIANTS_ARRAY)[number];

export const DS_PILL_SIZE_ARRAY = ['small', 'medium'] as const;
export type DsPillSize = (typeof DS_PILL_SIZE_ARRAY)[number];
@Component({
    selector: 'button[ds-pill], a[ds-pill]',
    template: `
        <ng-content select="[slot=start]" />
        <span class="ds-pill-text">
            <ng-content />
        </span>
        <ng-content select="[slot=end]" />
        <span class="ds-button-touch-target"></span>
    `,
    host: {
        'class': 'ds-pill',
        '[class]': 'hostClass()',
        '[class.ds-pill-selected]': 'selected()',
        '[class.ds-pill-disabled]': 'disabled()',
        '[attr.disabled]': 'disabled() ? true : null',
        '[class.ds-pill-rounded-padding]': 'roundedPadding()',
        '[class.ds-pill-inverse]': 'inverse()',
        '[attr.aria-disabled]': 'disabled()',
        '[attr.role]': "'button'",
        '[attr.aria-pressed]': 'selected() ? "true" : "false"',
        '[attr.aria-label]': 'ariaLabel() || "button pill"',
        '[attr.data-focus]': 'focused()',
        '[attr.data-focus-visible]': 'focusVisible()',
        '[attr.tabindex]': 'disabled() ? -1 : 0',
        '(keydown)': 'onKeydown($event)',
    },
    styleUrl: 'pill.component.scss',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsPill implements AfterViewInit, OnDestroy {
    readonly selected = input(false);
    readonly variant = model<DsPillVariant>('current');
    readonly size = model<DsPillSize>('medium');
    readonly ariaLabel = input<string>('');
    readonly disabled = input(false);
    readonly inverse = model(false);

    //TO DO use signal-based content queries
    /**
     * Retrieves and holds the list of child components or directives that implement the `DsAtoms` interface when
     * they are projected into the current component's content slots and are instances of the injection token `DS_ATOMS`.
     * If there are any children that implement `DsAtoms` interface are present, the host class `ds-pill-rounded-padding`
     * is added to the `ds-pill` component.
     */

    private readonly focused = signal(false);
    private readonly focusVisible = signal(false);

    private focusMonitor = inject(FocusMonitor);
    private elementRef = inject(ElementRef<HTMLElement>);

    readonly dsAtoms = contentChildren(DS_ATOMS, {
        descendants: true,
        read: ElementRef,
    });

    readonly roundedPadding = signal(false);

    readonly dsAtomsList = signal<ElementRef<HTMLElement>[]>([]);

    constructor() {
        // Watch for changes in dsAtoms and update the list
        effect(
            () => {
                const atoms = this.dsAtoms();
                this.dsAtomsList.set(atoms as ElementRef<HTMLElement>[]);
            },
            { allowSignalWrites: true },
        );

        effect(() => {
            const items = this.dsAtomsList();

            // Delay the check until after DOM updates (directive bindings like hidden)
            requestAnimationFrame(() => {
                const hasVisibleNotification = items.some((item) => {
                    const el = item.nativeElement;
                    return el.tagName.toLowerCase() === 'ds-notification-bubble' && !el.hasAttribute?.('hidden');
                });

                this.roundedPadding.set(hasVisibleNotification);
            });
        });
    }

    ngAfterViewInit() {
        this.focusMonitor.monitor(this.elementRef, true).subscribe((focusOrigin) => {
            if (focusOrigin) {
                this.focused.set(true);
            }
            if (focusOrigin === 'keyboard' || focusOrigin === 'program') {
                this.focusVisible.set(true);
            }
            if (!focusOrigin) {
                this.focusVisible.set(false);
                this.focused.set(false);
            }
        });
    }

    onKeydown(event: KeyboardEvent) {
        const { key } = event;
        if (key === ' ' || key === 'Enter') {
            //checks if anchor tag is used as a pill, if true, prevents default scrolling when using space as navigation tool
            if (this.elementRef.nativeElement.tagName === 'A') {
                event.preventDefault();
                (this.elementRef as ElementRef<HTMLAnchorElement>).nativeElement.click();
            }
        }
    }

    ngOnDestroy() {
        this.focusMonitor.stopMonitoring(this.elementRef);
    }

    protected readonly hostClass = computed(() => `ds-pill-${this.variant()} ds-pill-${this.size()}`);
}
